import { defineComponent } from 'vue'
import { mapActions, mapGetters } from 'vuex'
import {
  AnalyzerInterface,
  AnalyzerListActions,
  AnalyzerListGetters,
  TransformerInterface
} from '~/store/analyzer/list'
import { RepoConfigInterface } from '~/store/repository/detail'

const BASE_CONFIG: RepoConfigInterface = {
  version: 1,
  analyzers: [],
  test_patterns: [],
  exclude_patterns: []
}

export default defineComponent({
  name: 'ConfigGeneratorMixin',

  data() {
    return {
      // Create a deep copy of the base config to avoid modifying the original
      templateConfig: JSON.parse(JSON.stringify(BASE_CONFIG)) as RepoConfigInterface
    }
  },

  computed: {
    ...mapGetters('analyzer/list', {
      analyzerList: AnalyzerListGetters.ANALYZERS
    }),

    selectedAnalyzers(): string[] {
      return this.templateConfig.analyzers.map((analyzer) => analyzer.name)
    },

    activeAnalyzers(): AnalyzerInterface[] {
      return (this.analyzerList as AnalyzerInterface[])
        .filter((config) => this.selectedAnalyzers.includes(config.name))
        .sort((curr, next) => {
          const currIndex = this.selectedAnalyzers.indexOf(curr.name)
          const nextIndex = this.selectedAnalyzers.indexOf(next.name)
          return currIndex - nextIndex
        })
    }
  },

  methods: {
    ...mapActions('analyzer/list', {
      fetchAnalyzers: AnalyzerListActions.FETCH_ANALYZER_LIST
    }),

    addAnalyzer(analyzer: AnalyzerInterface): void {
      this.templateConfig.analyzers = this.templateConfig.analyzers.concat({
        name: analyzer.name,
        meta: {},
        enabled: true,
        type: analyzer.analyzerType
      })
    },

    removeAnalyzer(analyzerToRemove: AnalyzerInterface): void {
      this.templateConfig.analyzers = this.templateConfig.analyzers.filter(
        (analyzer) => analyzer.name !== analyzerToRemove.name
      )
    },

    syncAnalyzer(obj: AnalyzerInterface): void {
      this.templateConfig.analyzers = this.templateConfig.analyzers.map((analyzer) => {
        if (analyzer.name === obj.name) {
          Object.assign(analyzer, obj)
        }
        return analyzer
      })
    },

    syncTransformers(obj: Record<string, TransformerInterface>): void {
      const transformerKey = this.templateConfig['transformers']?.length
        ? 'transformers'
        : 'code_formatters'

      // Work with a copy to avoid mutating during iteration
      const workingArray = [...(this.templateConfig[transformerKey] || [])]
      const enabled = workingArray.map((transformer) => transformer.name)

      Object.keys(obj).forEach((name) => {
        if (!enabled.includes(name)) {
          workingArray.push(obj[name])
        }
      })

      const updatedArray = workingArray
        .map((transformer) => {
          if (Object.keys(obj).includes(transformer.name)) {
            Object.assign(transformer, obj[transformer.name])
          }
          return transformer
        })
        .filter((transformer) => transformer.enabled)

      // Create new templateConfig object to trigger reactivity
      this.templateConfig = {
        ...this.templateConfig,
        [transformerKey]: updatedArray
      }
    },

    validateConfig(collapseCardsIfRequired = true): boolean {
      let issueCount = 0
      ;(this.$children as unknown[]).forEach((child) => {
        const childProxy = child as { validateConfig?: (collapse: boolean) => number }

        if (childProxy.validateConfig) {
          issueCount += childProxy.validateConfig(collapseCardsIfRequired)
        }
      })

      return issueCount === 0
    }
  }
})
